import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from '../stylesheets/basicHeaderTabs.module.scss';

type TabId = 'unit' | 'transactions' | 'messages';

interface BasicHeaderTabsProps {
  unitLabel?: string;
  activeId?: TabId;
  onChange?: (tabId: TabId) => void;
  unitTabRef?: React.Ref<HTMLSpanElement>;
  transactionsTabRef?: React.Ref<HTMLSpanElement>;
  transactionsTabOnClick?: () => void;
  messagesTabRef?: React.Ref<HTMLSpanElement>;
}

const BasicHeaderTabs = ({
  unitLabel = 'Unit #3B',
  activeId,
  onChange,
  unitTabRef,
  transactionsTabRef,
  transactionsTabOnClick,
  messagesTabRef,
}: BasicHeaderTabsProps) => {
  const tabs = useMemo(
    () => [
      { id: 'unit' as const, label: unitLabel },
      { id: 'transactions' as const, label: 'Transactions' },
      { id: 'messages' as const, label: 'Messages' },
    ],
    [unitLabel]
  );
  const [internalActive, setInternalActive] = useState<TabId>(
    activeId ?? 'unit'
  );

  useEffect(() => {
    if (activeId) {
      setInternalActive(activeId);
    }
  }, [activeId]);

  const currentActive = activeId ?? internalActive;

  const handleSelect = (tabId: TabId) => {
    if (!activeId) {
      setInternalActive(tabId);
    }
    onChange?.(tabId);
  };

  const handleTabClick = (tabId: TabId) => {
    if (tabId === 'transactions' && transactionsTabOnClick) {
      transactionsTabOnClick();
      return;
    }
    handleSelect(tabId);
  };

  return (
    <div
      id="tab-bar-scroller--2"
      className={classNames('mdc-tab-bar-scroller', styles.tabScroller)}
    >
      <div className="mdc-tab-bar-scroller__scroll-frame">
        <nav
          className={classNames(
            'mdc-tab-bar',
            'mdc-tab-bar-scroller__scroll-frame__tabs',
            styles.tabBar
          )}
          role="tablist"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === currentActive;
            const tabLabelRef =
              tab.id === 'unit'
                ? unitTabRef
                : tab.id === 'transactions'
                  ? transactionsTabRef
                  : messagesTabRef;
            return (
              <button
                key={tab.id}
                type="button"
                className={classNames('mdc-tab', {
                  'mdc-tab--active': isActive,
                })}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabClick(tab.id)}
              >
                <span className={styles.tabLabel} ref={tabLabelRef}>
                  {tab.label}
                </span>
              </button>
            );
          })}
          <span className="mdc-tab-bar__indicator" />
        </nav>{' '}
      </div>
    </div>
  );
};

export default BasicHeaderTabs;
